import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Award,
  Calendar,
  MapPin,
  Users,
  Video,
  ExternalLink,
  ClipboardList,
  Trophy,
  Database,
} from "lucide-react";
import larryHughesHeroBg from "@/assets/larry-hughes-hero-bg.jpg";
import mylesColvinCamp from "@/assets/myles-colvin-camp.jpg";
import iversonClassicAnnouncement from "@/assets/iverson-classic-announcement.jpg";

export default function LarryHughesEliteCamp() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={larryHughesHeroBg}
            alt="College coaches courtside at Larry Hughes Elite Camp"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-secondary text-black px-6 py-2 font-black mb-6">INVITE ONLY</Badge>
          <h1 className="text-5xl lg:text-7xl font-black mb-2 leading-tight">
            RECRUITLOOK
            <span className="block text-secondary">HOOPS</span>
          </h1>
          <h2 className="text-3xl lg:text-5xl font-black">LARRY HUGHES ELITE CAMP</h2>
          <p className="text-gray-300 mt-6 max-w-3xl mx-auto">
            RecruitLook Hoops Presents Larry Hughes Elite Camp — a nationally covered, data-driven invite camp with a direct pathway to the Iverson Classic.
          </p>
        </div>
      </section>

      {/* CTA Buttons */}
      <section className="pt-8 pb-8 md:pt-12 md:pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 flex-wrap">
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/boys-circuit">BOYS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/girls-circuit">GIRLS CIRCUIT</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/larry-hughes-elite-camp">ELITE CAMP</a>
            </Button>
            <Button className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider w-full sm:w-auto" asChild>
              <a href="/juniors-circuit">JUNIORS CIRCUIT</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Key highlights */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-secondary font-black mb-2"><Award className="w-5 h-5" /> Since 2018</div>
            <ul className="list-disc pl-6 space-y-1 text-gray-700">
              <li>Invite only — 60 elite HS players each year</li>
              <li>Players from 20+ states</li>
              <li>National media coverage</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-secondary font-black mb-2"><Trophy className="w-5 h-5" /> Iverson Classic</div>
            <p className="text-gray-700">Only camp partnered with Allen Iverson's group. Camp MVP earns selection to the prestigious Iverson Classic.</p>
          </div>
        </div>
      </section>

      {/* Camp overview and image */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid lg:grid-cols-2 gap-6 items-stretch">
             <div className="bg-secondary text-slate-900 rounded-2xl p-4 sm:p-6 flex flex-col justify-center min-h-[300px] lg:h-[480px]">
              <h3 className="text-xl sm:text-2xl font-black mb-3">About Camp</h3>
               <p className="text-slate-900 mb-3 text-sm sm:text-base">Larry Hughes will be the lead instructor for the RecruitLook Hoops' Larry Hughes Elite Camp. Hughes and his Larry Hughes Basketball Academy staff will work with selected hoopers on basketball skills and drills, and also teaching valuable life skills.</p>
               <p className="text-slate-900 mb-3 text-sm sm:text-base">This camp features wearable technology to track everything athletes do on the court through Kinexon — giving college coaches a better idea of what you can do.</p>
               <p className="text-slate-900 text-sm sm:text-base">This is an invite-only camp for top performers from the RecruitLook Hoops League and select national recruits. You can nominate an athlete and our team will review it.</p>
            </div>
            <div className="rounded-2xl overflow-hidden min-h-[300px] lg:h-[480px]">
              <img src={mylesColvinCamp} alt="Myles Colvin at Larry Hughes Elite Camp" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Camp Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h4 className="text-2xl font-black mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-secondary" /> Camp Details</h4>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Who:</strong> 2025, 2026, & 2027 Top Basketball Recruits</li>
              <li><strong>When:</strong> August 17-18, 2024 — campers arrive Friday; camp begins Saturday morning</li>
              <li><strong>Where:</strong> St. Louis, MO</li>
              <li><strong>How:</strong> Invite Only</li>
              <li><strong>Cost:</strong> FREE</li>
              <li><strong>Location:</strong> Larry Hughes Basketball Academy, 140 Long Road, Suite 123, Chesterfield, MO 63005</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h4 className="text-2xl font-black mb-3 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-secondary" /> What's Included</h4>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Jersey top and RecruitLook Hoops apparel</li>
              <li>Snacks, Lunch (Sat & Sun), Dinner (Sat), and hotel Breakfast</li>
              <li>Elite instruction from former NBA player</li>
              <li>All camp data sent to college coaches</li>
              <li>RecruitLook scouting reports sent to college coaches</li>
              <li>Social media shoutouts and recruiting coverage</li>
              <li>Entire camp live streamed by BallerTV for coaches to view</li>
            </ul>
          </div>
        </div>
      </section>


      {/* Streaming and Data */}
      <section className="pt-6 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
            <h4 className="text-2xl font-black mb-3 flex items-center gap-2 justify-center"><Video className="w-5 h-5 text-secondary" /> Live Streaming</h4>
            <p className="text-gray-700 mb-4">ALL RecruitLook Hoops Events will be Live Streamed through BallerTV for College Coaches and Fans to enjoy!</p>
            <Button asChild variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-black">
              <a href="https://www.ballertv.com/organizations/recruitlook" target="_blank" rel="noopener noreferrer">Visit BallerTV.com/RecruitLook</a>
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h4 className="text-2xl font-black mb-3 flex items-center gap-2"><Database className="w-5 h-5 text-secondary" /> Data Analytics</h4>
            <p className="text-gray-700">The camp is one-of-a-kind in tracking athlete movement at Larry Hughes Basketball Academy. From RSPCT Basketball shot-tracking to Kinexon player movement to advanced ball-handling systems — all data flows into each athlete's digital locker for development and recruiting.</p>
          </div>
        </div>
      </section>

      {/* Iverson Classic */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h4 className="text-2xl font-black mb-3">Iverson Classic</h4>
            <p className="text-gray-700 mb-3">We're excited to announce our collaboration with the Iverson Classic and our Larry Hughes Elite Camp. Moving forward, the Larry Hughes Elite Camp will become an Iverson Classic evaluation and qualifier camp — the only camp in the country with such an honor.</p>
            <p className="text-gray-700 mb-4">The top rising senior performers from the Larry Hughes Elite Camp will be selected for the 24k Showcase Game during the Iverson Classic week. Additionally, representatives from the Iverson Classic will be in attendance at the Larry Hughes Elite Camp to extend invites to any camper that they feel meets the high standards of the Iverson Classic Game.</p>
            <h5 className="text-lg font-black mb-2">More About The Iverson Classic:</h5>
            <p className="text-gray-700">The Allen Iverson Roundball Classic, the #1 All-American game in the country by social viewership, held its first All-American game in 2017 and represents a disruptive, independent new force in the sports world. The AIRC is truly a unique experience. It's the only grassroots basketball event of its caliber that is run by a Hall of Fame legend who is hands-on, engaged, and involved directly with the players. These soon-to-be NBA stars actually train and compete under the guidance of Allen Iverson and other NBA legends. The week-long event is owned and operated by Iverson Manselle Bates Enterprises, a sports and entertainment company co-owned by Allen Iverson, Jai Manselle, and Bobby Bates.</p>
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img src={iversonClassicAnnouncement} alt="Iverson Classic announcement" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Alumni */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-3">CAMP ALUMNI</Badge>
            <h3 className="text-3xl lg:text-5xl font-black">Notable Alumni</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-slate-700">
            {alumni.map((a) => (
              <div key={a} className="bg-white border border-slate-200 rounded-xl p-3">{a}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">MEDIA COVERAGE</Badge>
            <h3 className="text-3xl lg:text-5xl font-black">Media Coverage Examples</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Award className="w-5 h-5" /> Recruiting Coverage</div>
              <p className="text-gray-700">A quick way to highlight a player that has performed well. We'll tweet before, during, and after events to promote players.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Video className="w-5 h-5" /> Video Highlights</div>
              <p className="text-gray-700">Our media team is filming games, editing clips, and pushing out highlight videos during the event for more exposure.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><MapPin className="w-5 h-5" /> Event Pics</div>
              <p className="text-gray-700">Our media team took pictures of all the teams that competed in RecruitLook Hoops events that were available for download and print.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const alumni: string[] = [
  "Michael Foster - Philadelphia 76ers",
  "Terry Armstrong - PRO (Australia)",
  "Adrian Griffin, Jr. - Atlanta Hawks",
  "Jamarion Sharp - Dallas Mavericks",
  "Franck Kepnang - Washington",
  "Xavier Foster - Iowa St.",
  "Tamar Bates - Mizzou",
  "Mac Etienne - UCLA",
  "Jimmy Bell - West Virginia",
  "Braeden Carrington - Tulsa",
  "DaJuan Gordon - Kansas St",
  "Tyler Cochran - Minnesota",
  "Matthew Stone - North Texas",
  "Caleb Grill - Mizzou",
  "Jayden Stone - Grand Canyon",
  "Tyson Acuff - Duquesne",
  "Jalen Harper - Florida Gulf Coast",
  "Graham Ike - Gonzaga",
  "Jack Molloy - Yale",
  "NJ Benson - DePaul",
  "Jermaine Hamlin - Illinois",
  "Daniel Tobiloba - South Florida",
  "Trey Williams - Missouri State",
  "Fischer Brown - Western Carolina",
  "John Clark - Texas",
  "Gevonte Ware - Rutgers",
  "Nicholas Randall - Mizzou",
  "Treyson Anderson - North Dakota St",
  "Riley Massey - North Dakota St",
  "Luke Walsh - Southern Illinois",
  "Ray'Sean Taylor - SIUE",
  "Mouhamed Dioubate - Alabama",
  "Josiah Dotzler - Creighton",
  "Rashad McDaniels - Central Michigan",
  "Latrell Wrightsell, Jr. - Alabama",
  "Braden Lamar - Western Illinois",
  "DaJion Humphrey - Holy Cross",
  "Antwaun Massey - Southern Illinois",
  "Langston Reynolds - Northern Colorado",
  "Harlan Obioha - Niagara",
  "Dallas Howell - North Alabama",
  "Zaide Lowery - Marquette",
  "Cole Farrell - Portland State",
  "Will Richardson - Fordham",
  "Braxton Bayless - Niagara",
  "Elijah Jones - Southern Indiana",
  "Jordan Pickett - SIUE",
  "Jayden Lemond - William & Mary",
  "Desmond White - Austin Peay",
  "Trey'd ez Green - LSU",
  "Connor Dow - Oklahoma State",
  "Deuce Roberts - Temple",
  "Tyrin Jones - UNLV",
  "Docker Tedeschi - Colorado State",
  "Keitenn Bristow - Tarleton State",
  "Bryce Spiller - Air Force",
  "Kaidon Rayfield - UTSA",
  "Dehrio McCaskill - SIUE",
  "Jamarion Batemon - Iowa State",
  "Devon Pearson - Texas A&M Corpus Christi",
];